"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import { Play, ArrowRight } from "lucide-react";

export default function AppElements() {
    return (
        <section className="relative bg-[#050505] text-white py-32 overflow-hidden min-h-[80vh] flex items-center">

            {/* Background Gradients */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-[120px] mix-blend-screen" />
                <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-[120px] mix-blend-screen" />
            </div>

            <div className="container mx-auto px-6 md:px-20 lg:px-40 relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">

                    {/* Left Column: Text Content */}
                    <div className="flex flex-col items-start gap-8 lg:gap-10">


                        {/* Main Quote / Text */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                        >
                            <h2 className="text-4xl md:text-5xl lg:text-7xl font-bold tracking-tighter text-white mb-6 leading-[0.9]">
                                User-Centric Design Philosophy
                            </h2>
                            <p className="text-xl md:text-2xl text-gray-400 font-light leading-relaxed max-w-xl">
                                We craft experiences that feel impossible. Driven by AI and guided by intuition, we turn complex logic into fluid, magical interactions.
                            </p>
                        </motion.div>

                    </div>

                    {/* Right Column: Featured Card */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, rotate: -2 }}
                        whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7, type: "spring", bounce: 0.3 }}
                        className="relative flex justify-center lg:justify-end"
                    >
                        {/* Glow effect behind card */}
                        <div className="absolute inset-0 bg-gradient-to-tr from-pink-500/20 to-blue-500/20 blur-3xl rounded-full transform scale-90" />

                        {/* The Card */}
                        <div className="relative w-[280px] sm:w-[340px] aspect-[9/19] bg-[#000] rounded-[40px] sm:rounded-[50px] overflow-hidden border-[8px] border-[#1a1a1a] shadow-2xl group cursor-pointer hover:-translate-y-2 transition-transform duration-500">

                            {/* Dynamic Island / Notch */}
                            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 sm:w-32 h-6 sm:h-7 bg-[#1a1a1a] rounded-b-xl z-20" />

                            {/* UI Content Layer */}
                            <div className="absolute inset-0 bg-[#0f0f11] flex flex-col">

                                {/* Status Bar */}
                                <div className="px-4 sm:px-6 pt-3 pb-2 flex justify-between items-center text-[10px] font-medium text-white">
                                    <span>9:41</span>
                                    <div className="flex gap-1.5 align-middle">
                                        <div className="w-3 h-3 rounded-full bg-white/20" />
                                        <div className="w-3 h-3 rounded-full bg-white/20" />
                                    </div>
                                </div>

                                {/* App Header */}
                                <div className="px-4 sm:px-6 py-4 sm:py-6 flex justify-between items-end">
                                    <div>
                                        <span className="text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider">Analytics</span>
                                        <h3 className="text-xl sm:text-2xl font-bold text-white mt-1">Overview</h3>
                                    </div>
                                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/10 flex items-center justify-center">
                                        <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-blue-500" />
                                    </div>
                                </div>

                                {/* Chart Area Mockup */}
                                <div className="px-4 sm:px-6 mb-6 sm:mb-8 relative">
                                    <div className="w-full h-32 sm:h-40 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-2xl sm:rounded-3xl border border-white/5 relative overflow-hidden p-4 sm:p-6 flex items-end justify-between gap-1.5 sm:gap-2">
                                        {[40, 70, 50, 90, 60, 80].map((h, i) => (
                                            <div key={i} className="w-full bg-blue-500 rounded-t-sm opacity-80" style={{ height: `${h}%` }} />
                                        ))}
                                    </div>
                                </div>

                                {/* Dynamic List Items */}
                                <div className="px-4 sm:px-6 flex flex-col gap-2.5 sm:gap-4">
                                    {[1, 2, 3].map((_, i) => (
                                        <div key={i} className={`items-center gap-3 sm:gap-4 p-2.5 sm:p-4 rounded-xl sm:rounded-2xl bg-white/5 border border-white/5 hover:bg-white/10 transition-colors ${i === 2 ? 'hidden sm:flex' : 'flex'}`}>
                                            <div className={`w-8 h-8 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center shrink-0 ${i === 0 ? 'bg-green-500/20 text-green-500' : i === 1 ? 'bg-orange-500/20 text-orange-500' : 'bg-purple-500/20 text-purple-500'}`}>
                                                <div className="w-4 h-4 sm:w-6 sm:h-6 rounded-full bg-current opacity-50" />
                                            </div>
                                            <div className="flex-1">
                                                <div className="h-1.5 sm:h-2 w-16 sm:w-24 bg-white/20 rounded-full mb-1 sm:mb-2" />
                                                <div className="h-1 sm:h-1.5 w-10 sm:w-16 bg-white/10 rounded-full" />
                                            </div>
                                            <div className="text-[9px] sm:text-xs font-mono text-gray-500">+24%</div>
                                        </div>
                                    ))}
                                </div>

                                {/* Bottom Tab Bar */}
                                <div className="mt-auto h-14 sm:h-20 bg-black/40 backdrop-blur-md border-t border-white/5 flex items-center justify-around px-2">
                                    {[1, 2, 3, 4].map((_, i) => (
                                        <div key={i} className={`w-8 h-8 sm:w-12 sm:h-12 rounded-full flex items-center justify-center ${i === 0 ? 'text-white' : 'text-gray-600'}`}>
                                            <div className="w-4 h-4 sm:w-6 sm:h-6 rounded-lg bg-current opacity-40" />
                                        </div>
                                    ))}
                                </div>
                            </div>

                        </div>

                        {/* Decor elements around card (optional) */}
                        <div className="absolute -right-8 top-1/4 w-16 h-16 bg-white/5 rounded-full blur-xl animate-pulse" />
                    </motion.div>

                </div>
            </div>
        </section>
    );
}
